import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { User, Mail, Phone, MapPin, Calendar, FileText, Briefcase, ArrowLeft, Eye } from "lucide-react";
import { toast } from "react-toastify";
import "./RegistrationStyles.css";

const url = process.env.REACT_APP_BACKEND_URL;

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    if (imagePath.startsWith("/uploads")) return `${url}${imagePath}`;
    return `${url}/uploads/${imagePath}`;
  };

  const fetchCustomerDetails = async () => {
    try {
      const [customerRes, docsRes, jobsRes] = await Promise.all([
        axios.get(`${url}/api/customer/${id}`),
        axios.get(`${url}/api/documents/customer/${id}`),
        axios.get(`${url}/api/jobavailabity/customer/${id}`)
      ]);

      setCustomer(customerRes.data.data);
      setDocuments(docsRes.data.data || []);
      setJobs(jobsRes.data.data || []);
    } catch (error) {
      toast.error("Failed to load customer details");
      navigate("/registration/customers");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading customer details...</div>;
  if (!customer) return <div className="loading">Customer not found</div>;

  return (
    <div className="customer-details-container">
      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedImage(null)}>&times;</button>
            <img src={selectedImage} alt="Preview" />
          </div>
        </div>
      )}

      <button onClick={() => navigate("/registration/customers")} className="back-btn">
        <ArrowLeft size={16} /> Back to List
      </button>

      <div className="customer-header">
        <div className="customer-avatar">
          <User size={40} />
        </div>
        <div className="customer-title">
          <h1>{customer.fullname}</h1>
          <p>Customer ID: {customer._id}</p>
        </div>
        <div className="customer-status">
          <span className={`status-badge ${customer.status}`}>
            {customer.status === "active" ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          <User size={16} /> Information
        </button>
        <button 
          className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <FileText size={16} /> Documents ({documents.length})
        </button>
        <button 
          className={`tab ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          <Briefcase size={16} /> Job Availability ({jobs.length})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'info' && (
          <div className="info-grid">
            <div className="info-card">
              <Mail size={20} />
              <div>
                <label>Email</label>
                <p>{customer.email}</p>
              </div>
            </div>
            <div className="info-card">
              <Phone size={20} />
              <div>
                <label>Phone</label>
                <p>{customer.phone}</p>
              </div>
            </div>
            <div className="info-card">
              <MapPin size={20} />
              <div>
                <label>Address</label>
                <p>{customer.address}</p>
              </div>
            </div>
            <div className="info-card">
              <Calendar size={20} />
              <div>
                <label>Registered On</label>
                <p>{new Date(customer.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="documents-list">
            {documents.length === 0 ? (
              <p className="no-data">No documents found for this customer</p>
            ) : (
              documents.map(doc => {
                const photoUrl = getFullImageUrl(doc.photos);
                const documentUrl = getFullImageUrl(doc.Documentupload);
                
                return (
                  <div key={doc._id} className="document-card">
                    <div className="document-header">
                      <h3>{doc.Document}</h3>
                      <span className="doc-no">#{doc.no}</span>
                    </div>
                    <div className="document-images">
                      {photoUrl ? (
                        <div className="document-image">
                          <img 
                            src={photoUrl} 
                            alt="Customer" 
                            onClick={() => setSelectedImage(photoUrl)}
                          />
                          <span>Photo</span>
                          <button 
                            className="view-btn"
                            onClick={() => setSelectedImage(photoUrl)}
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="document-image no-image">
                          <span>No Photo</span>
                        </div>
                      )}
                      {documentUrl ? (
                        <div className="document-image">
                          <img 
                            src={documentUrl} 
                            alt="Document" 
                            onClick={() => setSelectedImage(documentUrl)}
                          />
                          <span>Document</span>
                          <button 
                            className="view-btn"
                            onClick={() => setSelectedImage(documentUrl)}
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="document-image no-image">
                          <span>No Document</span>
                        </div>
                      )}
                    </div>
                    <div className="document-dates">
                      <div>
                        <label>Issue Date</label>
                        <p>{new Date(doc.issue_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <label>Expiry Date</label>
                        <p>{new Date(doc.expired_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="document-status">
                      <span className={`status-badge ${doc.status}`}>
                        {doc.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="jobs-list">
            {jobs.length === 0 ? (
              <p className="no-data">No job availabilities found for this customer</p>
            ) : (
              jobs.map(job => (
                <div key={job._id} className="job-card">
                  <div className="job-header">
                    <h3>{job.jobname?.category_name}</h3>
                    <span className="job-no">#{job.no}</span>
                  </div>
                  <div className="job-details">
                    <span className={`section-badge ${job.section?.toLowerCase().replace(' ', '-')}`}>
                      {job.section}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetails;